import {
  EmployeeHrmsStatus,
  EmployeeStates,
} from "../pages/settings/EmployeeDatasourceSettings";

export interface Result {
  resultCode: string;
  reason: string;
}
export type Role =
  | "ROLE_ADMIN"
  | "ROLE_OPTIONS_CREATOR"
  | "ROLE_OPTIONS_APPROVER"
  | "ADMIN_VIEWER"
  | "ROLE_ESOP_ADMIN"
  | "ROLE_ESOP_VIEWER";
export type GrantStates =
  | "CREATED"
  | "FOR_APPROVAL"
  | "APPROVED"
  | "OFFERED"
  | "OFFER_EXPIRED"
  | "GRANTED"
  | "WITHDRAW"
  | "NOT_APPROVED"
  | "CANCELLED";

export enum GrantState {
  CREATED = "CREATED",
  FOR_APPROVAL = "FOR_APPROVAL",
  APPROVED = "APPROVED",
  OFFERED = "OFFERED",
  OFFER_EXPIRED = "OFFER_EXPIRED",
  GRANTED = "GRANTED",
  WITHDRAW = "WITHDRAW",
  NOT_APPROVED = "NOT_APPROVED",
  CANCELLED = "CANCELLED",
}

export interface PlatformError {
  id: string;
  companyId: string;
  status: "unread" | "read";
  type?: "error" | "warning" | "info";
  operation?: string;
  entityType?: string;
  referenceIds?: string[];
  description: string;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}
export interface ReportStatus {
  status: "IN_PROGRESS" | "SUCCESS" | "FAILURE" | "QUEUED";
  reportName: string;
  reportType: HissaReportType;
  /**
   * Present only for CUSTOM_REPORT — scopes status updates to a specific
   * saved custom report. Multiple custom reports share reportType=CUSTOM_REPORT
   * so the id is required to distinguish them in the UI.
   */
  customReportId?: string;
  message?: string;
  additionalInfo?: any;
  error?: string;
  serverTime?: number;
}
export type InternalTransferMatchField =
  | "name"
  | "email"
  | "hrId"
  | "groupDateOfJoining";
export interface SyncSettings {
  syncFrequency: number;
  dataToSync: string[];
  considerResignationDateForLapsing: boolean;
  autoTransferToPersonalEmail: boolean;
  valuationForGrant?: string;
  vestingScheduleForGrant?: string;
  planForGrant?: string;
  strikePriceForGrant?: string;
  grantType?: string;
  name?: string;
  grantIdentifierTemplate?: string;
  additionalSettings: {
    employeeImportFilter?: {
      new?: {
        status: string[];
      };
      old?: {
        status: string[];
      };
    };
  };
  syncMode: "MANUAL" | "AUTOMATIC" | "";
  requireApprovalForResignation?: boolean;
  autoRaiseRequestForInternalTransfer?: boolean;
  internalTransferMatchFields?: InternalTransferMatchField[];
}

export const StatusOptions = [
  { value: EmployeeHrmsStatus.PENDING },
  { value: EmployeeHrmsStatus.ACTIVE },
  { value: EmployeeHrmsStatus.INACTIVE },
  { value: EmployeeHrmsStatus.RESIGNED },
  { value: EmployeeHrmsStatus.EMPLOYED },
];

export const EmployeeStateOptions = [
  { value: EmployeeStates.NEW_EMPLOYEES },
  { value: EmployeeStates.EXISTING },
  { value: EmployeeStates.ALL },
];

export enum HissaReportType {
  CORPORATE_ACTION = "CORPORATE_ACTION",
  ESOP_ACCOUNTING = "ESOP_ACCOUNTING",
  GRANT_REPORT = "GRANT_REPORT",
  EMPLOYEE_WISE_REPORT = "EMPLOYEE_WISE_REPORT",
  VEST_WISE_REPORT = "VEST_WISE_REPORT",
  CANCELLATION_REPORT = "CANCELLATION_REPORT",
  EXERCISE_REPORT = "EXERCISE_REPORT",
  LIQUIDITY_REPORT = "LIQUIDITY_REPORT",
  BUYBACK_REPORT = "BUYBACK_REPORT",
  POOL_STATEMENT = "POOL_STATEMENT",
  ACCEPTANCE_REPORT = "ACCEPTANCE_REPORT",
  FINANCIAL_REPORT = "FINANCIAL_REPORT",
  SH6_REPORT = "SH6_REPORT",
  SECTION_10_C_REPORT = "SECTION_10_C_REPORT",
  SEBI_REPORT = "SEBI_REPORT",
  CASHOUT_REPORT = "CASHOUT_REPORT",
  CUSTOM_REPORT = "CUSTOM_REPORT",
  EXPENSE_REPORT = "EXPENSE_REPORT",
}

export type ScheduleType = "INTERVAL" | "MONTHLY_DATE";

export interface ScheduleParams {
  recipients: string[];
  scheduleType: ScheduleType;
  /** Required when scheduleType === "INTERVAL" */
  intervalDays?: number;
  /** Required when scheduleType === "MONTHLY_DATE" — day of month 1-31.
   *  Days beyond the month's length clamp to the last day (e.g. 31 in Feb → 28). */
  monthlyDay?: number;
  /**
   * Optional ISO date string (YYYY-MM-DD). When provided and in the future:
   * - INTERVAL: first run fires on this date, then every intervalDays days.
   * - MONTHLY_DATE: first run on the first occurrence of monthlyDay ≥ this date.
   */
  startDate?: string;
  label?: string;
}

export type Schedulable<T> = T & { scheduleParams?: ScheduleParams };

export interface ScheduledReport {
  id: string;
  companyId: string;
  reportType: HissaReportType;
  params: Record<string, unknown>;
  recipients: string[];
  scheduleType: ScheduleType;
  intervalDays: number | null;
  monthlyDay: number | null;
  skippedDates: Array<
    string | { date: string; skippedBy?: string; skippedByName?: string }
  > | null;
  label: string | null;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface UpdateScheduleRequest {
  params?: Record<string, unknown>;
  recipients?: string[];
  scheduleType?: ScheduleType;
  intervalDays?: number;
  monthlyDay?: number;
  isActive?: boolean;
  reportType?: HissaReportType;
  label?: string;
  startDate?: string;
}

export interface ScheduledReportRunDelivery {
  userId: string;
  deliveredAt: string | null;
  failureReason: string | null;
}

export interface ScheduledReportRun {
  id: string;
  scheduledReportId: string;
  status: string;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  deliveries: ScheduledReportRunDelivery[];
}
